import { useState } from 'react';

import { Button, Input, Label } from '@poseidon/components/Input2';
import TipTapInput, { type TipTapDocument, type TipTapVariable } from '@poseidon/components/Input2/TipTapInput';
import { ManualSelectPure, type ReminderAction } from '@poseidon/components/ManualSelect';
import { General } from '@poseidon/components/StrokeIcons';

import type { SequenceAnyStepType, SequenceStep } from '~/components/Sequence/sequenceSteps';
import { DEFAULT_LIKE_COUNT, STEP_META, stepEditSpec } from '~/components/Sequence/sequenceSteps';

interface Props {
  /** The step being edited (controlled — the parent applies each change and passes it back). */
  step: SequenceStep;
  /** Live update as fields change (the parent commits locally; it persists on close). */
  onChange: (next: SequenceStep) => void;
  /** Close the editor (the parent persists the pending change). */
  onClose: () => void;
  /** `@`-variables (contact / user fields) offered in the body + subject editors. */
  variables?: TipTapVariable[];
  /** `/`-documents (the team's shareable docs) offered in the body + subject editors. */
  documents?: TipTapDocument[];
  /** The team's reusable reminder actions — the `reminder` step picks one from a dropdown (like campaigns). */
  reminderActions?: ReminderAction[];
  /** Whether the reminder-actions list is still loading. */
  reminderActionsLoading?: boolean;
  /** Create a new reminder action from the dropdown's "Create …" row. */
  onCreateReminder?: (name: string) => Promise<void>;
  /** Whether a reminder action is currently being created. */
  reminderCreating?: boolean;
  /**
   * Content-only mode (the per-contact locked graph): render ONLY the free-text fields a rep may override
   * for one contact — subject + body. The structural / non-text fields (`like` count, the `reminder` action
   * picker) are hidden, since a per-contact override tunes message text, not step configuration.
   */
  contentOnly?: boolean;
}

/** Field copy per step type — body label + placeholders. */
const BODY_LABEL: Partial<Record<SequenceAnyStepType, string>> = {
  message: 'Message',
  connect: 'Connection request note (optional)',
  inmail: 'Body',
  email: 'Body',
  call: 'Note (optional)',
  reminder: 'Note (optional)',
};
const BODY_PLACEHOLDER: Partial<Record<SequenceAnyStepType, string>> = {
  message: 'Write the message…',
  connect: 'Add a note to the connection request…',
  inmail: 'Write the InMail…',
  email: 'Write the email…',
  call: 'What to cover on the call…',
  reminder: 'What to do…',
};

/**
 * Inline editor card for a sequence step's content — the body / subject / like-count fields the step
 * type exposes (see {@link stepEditSpec}). Body + subject use the same {@link TipTapInput} rich editor
 * as `/templates` (HTML in / out), with `@`-variables, `/`-documents, and `{`-comments enabled.
 * Controlled: every field change flows through `onChange`, and the parent persists when the card
 * closes. Renders nothing for a step type with no editable content.
 *
 * The mention dropdowns portal outside this card (as `.tippy-box` popups), so the parent's
 * click-outside-to-close ignores them (see {@link SequenceGraphEditor}) — picking a variable/document
 * must not dismiss the editor.
 */
export function SequenceStepEditor({
  step,
  onChange,
  onClose,
  variables,
  documents,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  contentOnly = false,
}: Props) {
  const spec = stepEditSpec(step.type);
  // The like count is a local string so the field can be cleared / retyped freely; the parsed, clamped
  // value is committed to the step on each valid change. Seeded once (the editor is keyed per step).
  const [likeStr, setLikeStr] = useState(String(step.likeCount ?? DEFAULT_LIKE_COUNT));
  if (spec == null) return null;

  const onLikeChange = (raw: string) => {
    setLikeStr(raw);
    const n = parseInt(raw, 10);
    if (Number.isFinite(n)) onChange({ ...step, likeCount: Math.max(1, Math.min(50, n)) });
  };

  // InMail / email are long-form — allow rich formatting + links, like the email step in /templates.
  const rich = step.type === 'inmail' || step.type === 'email';
  // The call note is a plain-text instruction the rep reads on the to-do — the engine flattens it to text
  // and does NOT run the template evaluator, so DON'T offer `@`-variables / `/`-documents / `{`-comments
  // here (they'd be stored as raw, unresolved tokens). Touch-step bodies keep them. (Reminder uses the
  // action dropdown above, not a note.)
  const isTaskNote = step.type === 'call';

  return (
    <div
      data-step-editor
      className="w-80 rounded-xl border border-lynch-200 bg-white p-3 shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 [&>svg]:size-4">
          {STEP_META[step.type].icon}
          {STEP_META[step.type].label}
        </span>
        <Button size="2xs" text aria-label="Close" tooltip="Close" icon={<General.XMark />} onClick={onClose} />
      </div>

      {spec.subject === true && (
        <div className="mb-2 flex flex-col gap-1">
          <Label size="sm">Subject</Label>
          <TipTapInput
            placeholder="Subject line…"
            value={step.subject ?? ''}
            onChange={(html) => onChange({ ...step, subject: html })}
            variables={variables}
            documents={documents}
            showComments
            rows={1}
            autoFocus
          />
        </div>
      )}

      {/* A reminder picks a reusable team "reminder action" from a dropdown (parity with campaigns) — its
          name is stored on the step body and becomes the rep to-do's title. Its dropdown renders in a
          native `[popover]`, which the parent's click-outside guard ignores (see SequenceGraphEditor). */}
      {step.type === 'reminder' && !contentOnly && (
        <div className="flex flex-col gap-1">
          <Label size="sm">Action</Label>
          <ManualSelectPure
            value={step.body || undefined}
            reminderActions={reminderActions}
            isLoading={reminderActionsLoading}
            onCreateReminder={onCreateReminder}
            isCreating={reminderCreating}
            onChange={(name) => onChange({ ...step, body: name })}
            fullWidth
          />
        </div>
      )}

      {spec.body != null && spec.body !== false && step.type !== 'reminder' && (
        <div className="flex flex-col gap-1">
          <Label size="sm">{BODY_LABEL[step.type] ?? 'Body'}</Label>
          <TipTapInput
            placeholder={BODY_PLACEHOLDER[step.type] ?? 'Write…'}
            value={step.body ?? ''}
            onChange={(html) => onChange({ ...step, body: html })}
            variables={isTaskNote ? undefined : variables}
            documents={isTaskNote ? undefined : documents}
            showComments={!isTaskNote}
            allowFormatting={rich}
            allowLinks={rich}
            rows={4}
            autoFocus={spec.subject !== true}
          />
        </div>
      )}

      {spec.likeCount === true && !contentOnly && (
        <div className="flex flex-col gap-1">
          <Label size="sm">Posts to like</Label>
          <Input type="number" min={1} max={50} value={likeStr} onChange={(e) => onLikeChange(e.target.value)} />
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <Button size="sm" color="blue" icon={<General.Check />} onClick={onClose}>
          Save
        </Button>
      </div>
    </div>
  );
}
